import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'

type ToastType = 'success' | 'error' | 'info'
interface ToastMsg { id: number; type: ToastType; text1: string; text2?: string }

interface ToastApi {
  show: (opts: { type?: ToastType; text1: string; text2?: string; visibilityTime?: number }) => void
}

const ToastContext = createContext<ToastApi | null>(null)

// Module-level handle so non-component code (services) can toast too.
let globalShow: ToastApi['show'] | null = null
export const Toast = {
  show: (opts: Parameters<ToastApi['show']>[0] & { position?: 'top' | 'bottom' }) => globalShow?.(opts),
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([])
  const idRef = useRef(0)
  const show = useCallback<ToastApi['show']>(({ type = 'success', text1, text2, visibilityTime = 2800 }) => {
    const id = ++idRef.current
    setToasts((t) => [...t, { id, type, text1, text2 }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), visibilityTime)
  }, [])
  globalShow = show
  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="pointer-events-none fixed left-0 right-0 top-4 z-[100] flex flex-col items-center gap-sm px-md" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'pointer-events-auto w-full max-w-md animate-slide-up rounded-xl border-l-4 bg-surface px-md py-smd shadow-xl',
              t.type === 'success' && 'border-risk-safe',
              t.type === 'error' && 'border-risk-high',
              t.type === 'info' && 'border-primary',
            )}
          >
            <p className="text-body-sm font-semibold text-text">{t.text1}</p>
            {t.text2 ? <p className="text-caption text-text-secondary">{t.text2}</p> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
